import { useRef, useState } from 'react'
import { Upload } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useImportSchedule, type EventImportResponse } from '@/hooks/useEvents'
import { fmtDate } from '@/lib/dates'

const STATUS_LABELS: Record<string, string> = {
  created: 'Created',
  skipped_duplicate: 'Skipped (duplicate)',
  error: 'Error',
}

// Created = done (green), Error = failure (red), Skipped = nothing to do (muted).
const STATUS_CLS: Record<string, string> = {
  created: 'text-[var(--status-approved-fg)]',
  skipped_duplicate: 'text-[var(--mute)]',
  error: 'text-[var(--status-rejected-fg)]',
}

export function EventScheduleImport() {
  const importSchedule = useImportSchedule()
  const inputRef = useRef<HTMLInputElement>(null)
  const [result, setResult] = useState<EventImportResponse | null>(null)

  async function onFileChosen(file: File | undefined) {
    if (!file) return
    try {
      const res = await importSchedule.mutateAsync(file)
      setResult(res)
      if (res.errors > 0) {
        toast.error(`Imported ${res.created}, skipped ${res.skipped_duplicates}, ${res.errors} error(s)`)
      } else {
        toast.success(`Imported ${res.created} event(s), skipped ${res.skipped_duplicates} duplicate(s)`)
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Import failed')
    } finally {
      // Allow re-uploading the same file (e.g. after fixing rows).
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] p-3">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold">Import season schedule</h2>
        <div className="flex-1" />
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx"
          className="hidden"
          data-testid="schedule-file-input"
          onChange={(e) => onFileChosen(e.target.files?.[0])}
        />
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs"
          disabled={importSchedule.isPending}
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="mr-1 h-3 w-3" />
          {importSchedule.isPending ? 'Importing…' : 'Upload .xlsx / .csv'}
        </Button>
      </div>
      <p className="text-xs text-[var(--mute)] mt-1">
        Columns: location_name, event_date, duration_days, discipline. Already-registered
        weeks (same location + date) are skipped, so re-uploading is safe.
      </p>

      {result && (
        <div className="mt-2 overflow-auto border border-[var(--line-warm)]">
          <table className="text-xs w-full">
            <thead className="bg-[var(--surface-header)] border-b border-[var(--line-warm)]">
              <tr className="text-left text-[var(--mute)]">
                <th className="tbl-th w-10">Row</th>
                <th className="tbl-th">Location</th>
                <th className="tbl-th">Date</th>
                <th className="tbl-th">Result</th>
                <th className="tbl-th">Note</th>
              </tr>
            </thead>
            <tbody>
              {result.rows.map((r, i) => (
                <tr key={r.row_index} className={`border-b border-[var(--line-warm)] ${i % 2 === 1 ? 'bg-[var(--surface-zebra)]' : 'bg-[var(--surface-card)]'}`}>
                  <td className="px-2 py-1 tabular-nums">{r.row_index}</td>
                  <td className="px-2 py-1">{r.location_name || '—'}</td>
                  <td className="px-2 py-1 tabular-nums whitespace-nowrap">{fmtDate(r.event_date) || '—'}</td>
                  <td className={`px-2 py-1 ${STATUS_CLS[r.status] ?? ''}`}>
                    {STATUS_LABELS[r.status] ?? r.status}
                  </td>
                  <td className="px-2 py-1 text-[var(--mute)]">{r.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
